<template>
  <div class="min-h-screen bg-zinc-50 px-4 py-16 sm:px-6 lg:px-8">
    <div class="mx-auto w-full max-w-xl rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm sm:p-8">
      <div class="mb-5 flex items-center">
        <img
          src="@/assets/media/images/platforma-logo-in-color.svg"
          alt="Platforma"
          class="h-9 w-auto"
        >
      </div>
      <h1 class="text-3xl font-semibold text-zinc-900">Accept Staff Invitation</h1>
      <p class="mt-1 text-sm text-zinc-500">
        Complete your staff account setup to access this creator account.
      </p>

      <div v-if="errorMessage" class="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
        {{ successMessage }}
      </div>

      <div v-if="invite" class="mt-6 rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
        <p class="text-sm text-zinc-700">
          <span class="font-semibold text-zinc-900">Invited by:</span>
          {{ invite.creatorName || invite.creatorEmail || 'Creator account' }}
        </p>
        <p class="mt-1 text-sm text-zinc-700">
          <span class="font-semibold text-zinc-900">Email:</span>
          {{ invite.invitedEmail }}
        </p>
        <p class="mt-1 text-sm text-zinc-700">
          <span class="font-semibold text-zinc-900">Role:</span>
          {{ invite.role === 'admin' ? 'Admin' : 'Staff' }}
        </p>
      </div>

      <form class="mt-6 space-y-4" @submit.prevent="acceptInvite">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">First Name</label>
            <input
              v-model="form.firstName"
              type="text"
              class="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#2d8bff]"
            >
          </div>
          <div>
            <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Last Name</label>
            <input
              v-model="form.lastName"
              type="text"
              class="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#2d8bff]"
            >
          </div>
        </div>

        <div>
          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Create Password</label>
          <input
            v-model="form.password"
            type="password"
            minlength="8"
            required
            class="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#2d8bff]"
            placeholder="At least 8 characters"
          >
        </div>

        <div>
          <label class="block text-xs font-semibold uppercase tracking-wide text-zinc-500">Confirm Password</label>
          <input
            v-model="form.passwordConfirm"
            type="password"
            minlength="8"
            required
            class="mt-1 w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#2d8bff]"
            placeholder="Repeat password"
          >
        </div>

        <div class="pt-2">
          <button
            type="submit"
            class="w-full rounded-xl bg-[#2d8bff] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1b67c2] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="busy || !invite"
          >
            {{ busy ? 'Accepting...' : 'Accept Invitation' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const route = useRoute();
const router = useRouter();

const invite = ref(null);
const busy = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

const form = ref({
  firstName: '',
  lastName: '',
  password: '',
  passwordConfirm: '',
});

const token = ref('');

const showError = (message) => {
  errorMessage.value = message;
  successMessage.value = '';
};

const showSuccess = (message) => {
  successMessage.value = message;
  errorMessage.value = '';
};

const loadInvitePreview = async () => {
  token.value = String(route.query?.token || '').trim();
  if (!token.value) {
    showError('Invite token is missing.');
    return;
  }

  try {
    const response = await fetch(
      `${apiProxyUrl}/api/auth/staff/invite-preview?token=${encodeURIComponent(token.value)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Failed to load invitation.');
    }
    invite.value = data.invite || null;
    if (invite.value) {
      form.value.firstName = String(invite.value.invitedFirstName || '').trim();
      form.value.lastName = String(invite.value.invitedLastName || '').trim();
    }
  } catch (error) {
    showError(error.message || 'Failed to load invitation.');
  }
};

const acceptInvite = async () => {
  if (!invite.value) return;
  if (form.value.password !== form.value.passwordConfirm) {
    showError('Password confirmation does not match.');
    return;
  }
  busy.value = true;
  showError('');
  try {
    const response = await fetch(`${apiProxyUrl}/api/auth/staff/accept-invite`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: token.value,
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        password: form.value.password,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || 'Failed to accept invitation.');
    }
    showSuccess('Invite accepted. Redirecting to login...');
    setTimeout(() => {
      router.push({ name: 'Login' });
    }, 1200);
  } catch (error) {
    showError(error.message || 'Failed to accept invitation.');
  } finally {
    busy.value = false;
  }
};

onMounted(loadInvitePreview);
</script>
